/**
 * PEAK Comprehensive Assessment — lookup tables and derived-field helpers.
 *
 * All "Typical Age Score" fields are read-only and determined by the client's
 * age bracket. All "Age Range Equivalent" fields are read-only and determined
 * by the raw score entered for that subscale. Neither type of derived field
 * is sent to the backend.
 */

export type PeakAgeRange =
  | "1-2 Years"
  | "3-4 Years"
  | "5-6 Years"
  | "7-8 Years"
  | "9-10 Years"
  | "11-12 Years"
  | "13-14 Years"
  | "15-16 Years";

/** Returns the age bracket label for a given age in years. */
export function getPeakAgeRange(ageInYears: number | null): PeakAgeRange | null {
  if (ageInYears === null) return null;
  if (ageInYears <= 2) return "1-2 Years";
  if (ageInYears <= 4) return "3-4 Years";
  if (ageInYears <= 6) return "5-6 Years";
  if (ageInYears <= 8) return "7-8 Years";
  if (ageInYears <= 10) return "9-10 Years";
  if (ageInYears <= 12) return "11-12 Years";
  if (ageInYears <= 14) return "13-14 Years";
  return "15-16 Years";
}

// ─── Typical Age Scores (by client age range) ────────────────────────────────

const FOUNDATIONAL_LEARNING_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 2,
  "3-4 Years": 30,
  "5-6 Years": 34,
  "7-8 Years": 34,
  "9-10 Years": 34,
};

const PERCEPTUAL_LEARNING_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 0,
  "3-4 Years": 18,
  "5-6 Years": 21,
  "7-8 Years": 22,
  "9-10 Years": 22,
};

const VERBAL_COMPREHENSION_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 0,
  "3-4 Years": 19,
  "5-6 Years": 80,
  "7-8 Years": 94,
  "9-10 Years": 100,
};

const VERBAL_REASONING_MEMORY_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 0,
  "3-4 Years": 0,
  "5-6 Years": 10,
  "7-8 Years": 22,
  "9-10 Years": 28,
};

const FOUNDATIONAL_LEARNING_BASIC_SOCIAL_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 1,
  "3-4 Years": 20,
  "5-6 Years": 25,
  "7-8 Years": 26,
  "9-10 Years": 28,
  "11-12 Years": 29,
  "13-14 Years": 33,
  "15-16 Years": 33,
};

const BASIC_VERBAL_COMPREHENSION_ADVANCED_SOCIAL_TYPICAL_AGE: Partial<
  Record<PeakAgeRange, number>
> = {
  "1-2 Years": 1,
  "3-4 Years": 15,
  "5-6 Years": 24,
  "7-8 Years": 36,
  "9-10 Years": 55,
  "11-12 Years": 57,
  "13-14 Years": 58,
  "15-16 Years": 59,
};

const ADVANCED_VERBAL_COMPREHENSION_ADVANCED_MATH_TYPICAL_AGE: Partial<
  Record<PeakAgeRange, number>
> = {
  "1-2 Years": 2,
  "3-4 Years": 4,
  "5-6 Years": 9,
  "7-8 Years": 13,
  "9-10 Years": 50,
  "11-12 Years": 53,
  "13-14 Years": 61,
  "15-16 Years": 63,
};

const VERBAL_REASONING_ADVANCED_READING_WRITING_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> =
  {
    "1-2 Years": 0,
    "3-4 Years": 0,
    "5-6 Years": 0,
    "7-8 Years": 0,
    "9-10 Years": 16,
    "11-12 Years": 20,
    "13-14 Years": 26,
    "15-16 Years": 29,
  };

const DIRECT_TEACHING_TOTAL_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 2,
  "3-4 Years": 67,
  "5-6 Years": 145,
  "7-8 Years": 172,
  "9-10 Years": 184,
};

const GENERALIZATION_TOTAL_TYPICAL_AGE: Partial<Record<PeakAgeRange, number>> = {
  "1-2 Years": 4,
  "3-4 Years": 39,
  "5-6 Years": 58,
  "7-8 Years": 75,
  "9-10 Years": 149,
  "11-12 Years": 159,
  "13-14 Years": 178,
  "15-16 Years": 184,
};

/**
 * Returns the Typical Age Score for a given subscale and client age bracket.
 * Returns null when the age bracket is not in the table for that subscale.
 */
export function getTypicalAgeScore(
  field:
    | "foundationalLearning"
    | "perceptualLearning"
    | "verbalComprehension"
    | "verbalReasoningMemory"
    | "foundationalLearningBasicSocialSkills"
    | "basicVerbalComprehensionAdvancedSocialSkills"
    | "advancedVerbalComprehensionAdvancedMathSkills"
    | "verbalReasoningAdvancedReadingWritingSkills"
    | "directTeachingTotal"
    | "generalizationTotal",
  ageRange: PeakAgeRange | null
): number | null {
  if (!ageRange) return null;
  const tables: Record<string, Partial<Record<PeakAgeRange, number>>> = {
    foundationalLearning: FOUNDATIONAL_LEARNING_TYPICAL_AGE,
    perceptualLearning: PERCEPTUAL_LEARNING_TYPICAL_AGE,
    verbalComprehension: VERBAL_COMPREHENSION_TYPICAL_AGE,
    verbalReasoningMemory: VERBAL_REASONING_MEMORY_TYPICAL_AGE,
    foundationalLearningBasicSocialSkills: FOUNDATIONAL_LEARNING_BASIC_SOCIAL_TYPICAL_AGE,
    basicVerbalComprehensionAdvancedSocialSkills:
      BASIC_VERBAL_COMPREHENSION_ADVANCED_SOCIAL_TYPICAL_AGE,
    advancedVerbalComprehensionAdvancedMathSkills:
      ADVANCED_VERBAL_COMPREHENSION_ADVANCED_MATH_TYPICAL_AGE,
    verbalReasoningAdvancedReadingWritingSkills:
      VERBAL_REASONING_ADVANCED_READING_WRITING_TYPICAL_AGE,
    directTeachingTotal: DIRECT_TEACHING_TOTAL_TYPICAL_AGE,
    generalizationTotal: GENERALIZATION_TOTAL_TYPICAL_AGE,
  };
  const val = tables[field]?.[ageRange];
  return val !== undefined ? val : null;
}

// ─── Age Range Equivalents (by raw score) ────────────────────────────────────

/** Maps a Foundational Learning score to its Age Range Equivalent. */
export function getFoundationalLearningAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score <= 2) return "1-2 Years";
  if (score <= 29) return "3-4 Years";
  if (score <= 34) return "5-6 Years";
  return "7-8 Years";
}

/** Maps a Perceptual Learning score to its Age Range Equivalent. */
export function getPerceptualLearningAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score === 0) return "1-2 Years";
  if (score <= 17) return "3-4 Years";
  if (score <= 21) return "5-6 Years";
  if (score === 22) return "7-8 Years";
  return "9-10 Years";
}

/** Maps a Verbal Comprehension score to its Age Range Equivalent. */
export function getVerbalComprehensionAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score === 0) return "1-2 Years";
  if (score <= 18) return "3-4 Years";
  if (score <= 79) return "5-6 Years";
  if (score <= 93) return "7-8 Years";
  return "9-10 Years";
}

/** Maps a Verbal Reasoning & Memory score to its Age Range Equivalent. */
export function getVerbalReasoningMemoryAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score === 0) return "1-2 Years";
  if (score <= 10) return "5-6 Years";
  if (score <= 22) return "7-8 Years";
  return "9-10 Years";
}

/** Maps a Foundational Learning & Basic Social Skills score to its Age Range Equivalent. */
export function getFoundationalLearningBasicSocialAgeRange(
  score: number | null
): PeakAgeRange | null {
  if (score === null) return null;
  if (score <= 1) return "1-2 Years";
  if (score <= 19) return "3-4 Years";
  if (score <= 25) return "5-6 Years";
  if (score === 26) return "7-8 Years";
  if (score === 27) return "9-10 Years";
  if (score <= 29) return "11-12 Years";
  if (score <= 33) return "13-14 Years";
  return "15-16 Years";
}

/** Maps an Advanced Verbal Comprehension, Basic Problem Solving, & Advanced Math Skills score
 *  to its Age Range Equivalent. */
export function getAdvancedVerbalComprehensionAdvancedMathAgeRange(
  score: number | null
): PeakAgeRange | null {
  if (score === null) return null;
  if (score <= 2) return "1-2 Years";
  if (score <= 4) return "3-4 Years";
  if (score <= 9) return "5-6 Years";
  if (score <= 13) return "7-8 Years";
  if (score <= 50) return "9-10 Years";
  if (score <= 53) return "11-12 Years";
  if (score <= 61) return "13-14 Years";
  return "15-16 Years";
}

/** Maps a Verbal Reasoning, Advanced Problem Solving, & Advanced Reading & Writing Skills score
 *  to its Age Range Equivalent. */
export function getVerbalReasoningAdvancedReadingWritingAgeRange(
  score: number | null
): PeakAgeRange | null {
  if (score === null) return null;
  if (score === 0) return "1-2 Years";
  if (score <= 16) return "9-10 Years";
  if (score <= 20) return "11-12 Years";
  if (score <= 26) return "13-14 Years";
  return "15-16 Years";
}

/** Maps a Direct Teaching Total Score to its Age Range Equivalent. */
export function getDirectTeachingAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score <= 2) return "1-2 Years";
  if (score <= 67) return "3-4 Years";
  if (score <= 145) return "5-6 Years";
  if (score <= 172) return "7-8 Years";
  return "9-10 Years";
}

/** Maps a Generalization Total Score to its Age Range Equivalent. */
export function getGeneralizationAgeRange(score: number | null): PeakAgeRange | null {
  if (score === null) return null;
  if (score <= 4) return "1-2 Years";
  if (score <= 39) return "3-4 Years";
  if (score <= 58) return "5-6 Years";
  if (score <= 75) return "7-8 Years";
  if (score <= 149) return "9-10 Years";
  if (score <= 159) return "11-12 Years";
  if (score <= 178) return "13-14 Years";
  return "15-16 Years";
}

// ─── Enum Converter ─────────────────────────────────────────────────────────

const PEAK_AGE_RANGE_TO_ENUM: Record<PeakAgeRange, string> = {
  "1-2 Years": "OneToTwo",
  "3-4 Years": "ThreeToFour",
  "5-6 Years": "FiveToSix",
  "7-8 Years": "SevenToEight",
  "9-10 Years": "NineToTen",
  "11-12 Years": "ElevenToTwelve",
  "13-14 Years": "ThirteenToFourteen",
  "15-16 Years": "FifteenToSixteen",
};

/**
 * Converts a display age-range string (e.g. "1-2 Years") to the Prisma
 * PeakAgeRangeEquivalent enum value accepted by the backend (e.g. "OneToTwo").
 */
export function peakAgeRangeToEnum(ageRange: PeakAgeRange | null): string | null {
  if (ageRange === null) return null;
  return PEAK_AGE_RANGE_TO_ENUM[ageRange] ?? null;
}

/** Returns (typicalAgeScore - score), or null if either is null. */
export function getDifferenceInScore(
  typicalAgeScore: number | null,
  score: number | null
): number | null {
  if (typicalAgeScore === null || score === null) return null;
  return typicalAgeScore - score;
}

/** Sums an array of scores, treating null as 0. Returns null if all are null. */
export function sumScores(scores: (number | null)[]): number | null {
  const nonNull = scores.filter((s): s is number => s !== null);
  if (nonNull.length === 0) return null;
  return nonNull.reduce((a, b) => a + b, 0);
}
